import React, { createContext, useState, useEffect, ReactNode } from 'react';
import { API_BASE_URL } from '../config/api';
import { cognitoConfig, getCognitoHostedUIUrl } from '../config/cognito';

export interface User {
  email: string;
  name?: string;
  picture?: string;
}

export interface AuthTokens {
  accessToken: string;
  refreshToken?: string;
  idToken?: string;
}

export interface AuthContextValue {
  user: User | null;
  loading: boolean;
  isAuthenticated: boolean;
  isAdmin: boolean;
  signInWithGoogle: () => void;
  handleGoogleCallback: (code: string) => Promise<void>;
  logout: () => void;
  getAuthHeaders: () => Record<string, string>;
}

export const AuthContext = createContext<AuthContextValue | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

/**
 * Decode JWT payload (without verification)
 */
function decodeJwtPayload(token: string): Record<string, unknown> | null {
  try {
    const parts = token.split('.');
    if (parts.length !== 3) return null;
    const payload = parts[1];
    const decoded = atob(payload.replace(/-/g, '+').replace(/_/g, '/'));
    return JSON.parse(decoded);
  } catch {
    return null;
  }
}

/**
 * Check if user has admin group in their token
 */
function hasAdminGroup(idToken: string): boolean {
  const payload = decodeJwtPayload(idToken);
  if (!payload) return false;

  const groups = payload['cognito:groups'];
  if (Array.isArray(groups)) {
    return groups.includes('admin');
  }
  if (typeof groups === 'string') {
    // Handle API Gateway serialization quirk: "[admin service-accounts]"
    if (groups.startsWith('[') && groups.endsWith(']')) {
      const groupList = groups.slice(1, -1).split(' ').filter(g => g.length > 0);
      return groupList.includes('admin');
    }
    return groups === 'admin';
  }
  return false;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);

  useEffect(() => {
    const storedUser = localStorage.getItem('user');
    const tokens = getStoredTokens();

    if (storedUser && tokens?.accessToken && tokens?.idToken) {
      try {
        const admin = hasAdminGroup(tokens.idToken);
        setUser(JSON.parse(storedUser));
        setIsAdmin(admin);
      } catch (error) {
        console.error('Invalid stored user data:', error);
        clearAuth();
      }
    }
    setLoading(false);
  }, []);

  const getStoredTokens = (): AuthTokens | null => {
    try {
      const tokens = localStorage.getItem('auth_tokens');
      return tokens ? JSON.parse(tokens) : null;
    } catch {
      return null;
    }
  };

  const storeTokens = (tokens: AuthTokens): void => {
    localStorage.setItem('auth_tokens', JSON.stringify(tokens));
  };

  const clearAuth = (): void => {
    localStorage.removeItem('user');
    localStorage.removeItem('auth_tokens');
    setUser(null);
    setIsAdmin(false);
  };

  const signInWithGoogle = (): void => {
    const hostedUIUrl = getCognitoHostedUIUrl();
    const redirectUri = `${window.location.origin}/auth/google/callback`;

    const params = new URLSearchParams({
      client_id: cognitoConfig.clientId,
      response_type: 'code',
      scope: 'email openid profile',
      redirect_uri: redirectUri,
      identity_provider: 'Google',
    });

    const authorizeUrl = `${hostedUIUrl}/oauth2/authorize?${params.toString()}`;
    window.location.href = authorizeUrl;
  };

  const handleGoogleCallback = async (code: string): Promise<void> => {
    try {
      const url = new URL('auth/google/callback', API_BASE_URL);

      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ code }),
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || 'Failed to authenticate with Google');
      }

      const data = await response.json();

      if (data.tokens && data.user) {
        // Transform snake_case from API to camelCase for frontend
        const tokens: AuthTokens = {
          accessToken: data.tokens.access_token,
          idToken: data.tokens.id_token,
          refreshToken: data.tokens.refresh_token,
        };
        storeTokens(tokens);

        // Check if user is admin
        const admin = tokens.idToken ? hasAdminGroup(tokens.idToken) : false;

        const userData: User = {
          email: data.user.email,
          name: data.user.name,
          picture: data.user.picture,
        };
        localStorage.setItem('user', JSON.stringify(userData));
        setUser(userData);
        setIsAdmin(admin);
      }
    } catch (error) {
      console.error('Google OAuth callback error:', error);
      throw error;
    }
  };

  const logout = (): void => {
    clearAuth();
  };

  const getAuthHeaders = (): Record<string, string> => {
    const tokens = getStoredTokens();
    return tokens?.accessToken
      ? { Authorization: `Bearer ${tokens.accessToken}` }
      : {};
  };

  const isAuthenticated = !!user && !!getStoredTokens()?.accessToken;

  const value: AuthContextValue = {
    user,
    loading,
    isAuthenticated,
    isAdmin,
    signInWithGoogle,
    handleGoogleCallback,
    logout,
    getAuthHeaders,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
